import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "@components";
import {EventPaymentMethod, EventPaymentSettings, EventPaymentType, Lookups} from "@model";
import {EventPaymentSettingsService, LoaderService, TOAST_STATE, ToastService} from "@services";
import {numerical, required} from "@utility";

@Component({
  selector: 'app-event-payment-settings',
  templateUrl: './event-payment-settings.component.html',
  styleUrls: ['./event-payment-settings.component.scss']
})
export class EventPaymentSettingsComponent extends AbstractComponent implements OnInit {

  @Input() currentPaymentSettings: EventPaymentSettings | undefined;
  @Input() eventId: number | undefined;
  @Input() disableEditing: boolean = true;
  @Input() paymentMethods: Lookups[] = [];
  @Input() paymentReminderMethods: Lookups[] = [];
  @Input() paymentTypes: Lookups[] = [];

  @Output() paymentSettingsSaved: EventEmitter<EventPaymentSettings> = new EventEmitter<EventPaymentSettings>();

  editForm: boolean = false;

  paymentReminderMethodsOptions: Map<number, string> = new Map<number, string>();

  inputForm = new FormGroup({
    mandatoryPayment: new FormControl(),
    paymentMethod: new FormControl(),
    createBarcode: new FormControl(),
    paymentType: new FormControl(),
    issuingInvoice: new FormControl(),
    validHours: new FormControl(),
    paymentReminder: new FormControl(),
    days: new FormControl(),
    hours: new FormControl(),
    reminderMessage: new FormControl(),
    reminderMethod: new FormControl(),
  });

  constructor(private paymentSettingsService: EventPaymentSettingsService, private toastService: ToastService,
              public translocoService: TranslocoService, private loaderService: LoaderService,) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.currentPaymentSettingsChanged();
    if (this.paymentReminderMethods) {
      const optionsMap = new Map<number, string>();
      this.paymentReminderMethods.map((reminderMethod: Lookups) => {
        if (reminderMethod.id) {
          optionsMap.set(reminderMethod.id, this.translocoService.translate("event.payment.settings.reminder.delivery.method." + reminderMethod.id))
        }
      });
      this.paymentReminderMethodsOptions = optionsMap;
    }
  }

  get paymentTypesOptions() {
    let paymentTypesOptions: Map<number, string> = new Map<number, string>();
    if ((this.paymentTypes?.length ?? 0) > 0) {
      const optionsMap: Map<number, string> = new Map<number, string>();
      for (let i = 0; i < this.paymentTypes!.length; i++) {
        if (this.paymentTypes[i].id === EventPaymentType.ONLINE_PAYMENT && this.inputForm.controls.paymentMethod.value === EventPaymentMethod.TRACEY) {
          optionsMap.set(this.paymentTypes[i].id!, this.translocoService.translate("event.payment.settings.payment.type." + this.paymentTypes[i].id))
        } else if (this.paymentTypes[i].id === EventPaymentType.PAYMENT_BY_BANK_TRANSFER) {
          optionsMap.set(this.paymentTypes[i].id!, this.translocoService.translate("event.payment.settings.payment.type." + this.paymentTypes[i].id))
        }
      }
      paymentTypesOptions = optionsMap
    }
    return paymentTypesOptions;
  }

  private subscribeToMandatoryPaymentFieldChange() {
    this.subscriptions.push(
      this.inputForm.controls.mandatoryPayment.valueChanges.subscribe((value) => {
        if (value === true) {
          this.inputForm.controls.paymentMethod.setValue(EventPaymentMethod.TRACEY);
          this.inputForm.controls.paymentMethod.disable();
          this.inputForm.controls.validHours.disable();
          this.inputForm.controls.validHours.removeValidators([required]);
          this.inputForm.controls.validHours.setValue(undefined);
          this.inputForm.controls.paymentReminder.setValue(undefined);
          this.inputForm.controls.paymentReminder.disable();
          this.inputForm.controls.reminderMessage.setValue(undefined);
          this.inputForm.controls.reminderMethod.setValue(undefined);
          this.inputForm.controls.paymentType.setValue([EventPaymentType.ONLINE_PAYMENT])
          this.inputForm.controls.paymentType.disable();
          this.inputForm.controls.paymentType.markAsUntouched()
          this.inputForm.controls.paymentType.updateValueAndValidity();
        } else {
          this.inputForm.controls.paymentMethod.enable();
          this.inputForm.controls.paymentReminder.enable();
          if (this.inputForm.controls.paymentMethod.value === EventPaymentMethod.TRACEY) {
            this.inputForm.controls.validHours.enable();
            this.inputForm.controls.validHours.addValidators([required]);
          }
        }
      })
    );
  }

  private subscribeToPaymentMethodFieldChange() {
    this.subscriptions.push(
      this.inputForm.controls.paymentMethod.valueChanges.subscribe((value) => {
        if (value === EventPaymentMethod.ORGANISER) {
          this.inputForm.controls.createBarcode.enable();
          this.inputForm.controls.createBarcode.setValue(false);
        } else {
          this.inputForm.controls.createBarcode.disable();
          this.inputForm.controls.createBarcode.setValue(undefined);
        }
        if (value === EventPaymentMethod.TRACEY) {
          this.inputForm.controls.paymentType.enable()
          this.inputForm.controls.paymentType.markAsUntouched()
          this.inputForm.controls.paymentType.addValidators([required]);
          this.inputForm.controls.paymentType.updateValueAndValidity();

          this.inputForm.controls.issuingInvoice.enable();
          this.inputForm.controls.issuingInvoice.addValidators([required]);
          this.inputForm.controls.issuingInvoice.updateValueAndValidity();
          this.inputForm.controls.validHours.enable();
          if (!this.inputForm.controls.mandatoryPayment.value) {
            this.inputForm.controls.validHours.addValidators([required]);
            this.inputForm.controls.validHours.updateValueAndValidity();
          }
        } else {
          this.inputForm.controls.paymentType.disable()
          this.inputForm.controls.paymentType.setValue(undefined);
          this.inputForm.controls.paymentType.removeValidators([required]);
          this.inputForm.controls.issuingInvoice.disable();
          this.inputForm.controls.issuingInvoice.removeValidators([required]);
          this.inputForm.controls.issuingInvoice.setValue(false);
          this.inputForm.controls.validHours.disable();
          this.inputForm.controls.validHours.removeValidators([required]);
          this.inputForm.controls.validHours.setValue(undefined);
        }
      })
    );
  }

  private subscribeToPaymentReminderFieldChange() {
    this.subscriptions.push(this.inputForm.controls.paymentReminder.valueChanges.subscribe(value => {
      if (value === true) {
        this.inputForm.controls.reminderMessage.enable();
        this.inputForm.controls.reminderMessage.addValidators([required]);
        this.inputForm.controls.reminderMessage.markAsUntouched();
        this.inputForm.controls.days.enable();
        this.inputForm.controls.days.addValidators([required]);
        this.inputForm.controls.days.markAsUntouched();
        this.inputForm.controls.hours.enable();
        this.inputForm.controls.hours.addValidators([required]);
        this.inputForm.controls.hours.markAsUntouched();
        this.inputForm.controls.reminderMethod.enable();
        this.inputForm.controls.reminderMethod.addValidators([required]);
        this.inputForm.controls.reminderMethod.markAsUntouched();
      } else {
        this.inputForm.controls.reminderMessage.disable();
        this.inputForm.controls.reminderMessage.removeValidators([required]);
        this.inputForm.controls.reminderMessage.setValue(undefined);
        this.inputForm.controls.days.disable();
        this.inputForm.controls.days.removeValidators([required]);
        this.inputForm.controls.days.setValue(undefined);
        this.inputForm.controls.hours.disable();
        this.inputForm.controls.hours.removeValidators([required]);
        this.inputForm.controls.hours.setValue(undefined);
        this.inputForm.controls.reminderMethod.disable();
        this.inputForm.controls.reminderMethod.removeValidators([required]);
        this.inputForm.controls.reminderMethod.setValue(undefined);
      }
      this.inputForm.controls.reminderMessage.updateValueAndValidity();
      this.inputForm.controls.days.updateValueAndValidity();
      this.inputForm.controls.hours.updateValueAndValidity();
      this.inputForm.controls.reminderMethod.updateValueAndValidity();
    }));
  }

  private initForm() {
    this.inputForm.controls.validHours.addValidators([numerical]);
    this.inputForm.controls.mandatoryPayment.addValidators([required]);
    this.inputForm.controls.paymentMethod.addValidators([required]);

    this.subscribeToMandatoryPaymentFieldChange();
    this.subscribeToPaymentMethodFieldChange();
    this.subscribeToPaymentReminderFieldChange();
    if (this.currentPaymentSettings?.eventId) {
      this.inputForm.patchValue(this.currentPaymentSettings);
      this.inputForm.updateValueAndValidity();
    } else {
      this.inputForm.reset();
      this.inputForm.controls.mandatoryPayment.setValue(false);
    }
    this.subscriptions.push(this.inputForm.valueChanges.subscribe(() => {
      this.inputForm.markAsTouched();
    }));
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  private currentPaymentSettingsChanged() {
    if (this.currentPaymentSettings) {
      this.editForm = true;
    } else if (this.eventId) {
      this.currentPaymentSettings = new EventPaymentSettings();
      this.currentPaymentSettings.eventId = this.eventId;
      this.editForm = false;
    }
  }


  getPaymentSettingsFields(): EventPaymentSettings {
    this.checkValidators(this.inputForm);
    let paymentSettings: EventPaymentSettings = {...this.currentPaymentSettings}
    paymentSettings.eventId = this.eventId;
    paymentSettings.mandatoryPayment = this.inputForm.controls.mandatoryPayment.value;
    paymentSettings.paymentMethod = this.inputForm.controls.paymentMethod.value;
    paymentSettings.createBarcode = this.inputForm.controls.createBarcode.value;
    paymentSettings.paymentType = this.inputForm.controls.paymentType.value;
    paymentSettings.issuingInvoice = this.inputForm.controls.issuingInvoice.value;
    paymentSettings.validHours = this.inputForm.controls.validHours.value;
    paymentSettings.paymentReminder = this.inputForm.controls.paymentReminder.value;
    paymentSettings.days = this.inputForm.controls.days.value;
    paymentSettings.hours = this.inputForm.controls.hours.value;
    paymentSettings.reminderMessage = this.inputForm.controls.reminderMessage.value;
    paymentSettings.reminderMethod = this.inputForm.controls.reminderMethod.value;
    return paymentSettings;
  }

  onSave() {
    if (!this.inputForm.valid || this.disableEditing) {
      for (const control in this.inputForm.controls) {
        this.inputForm.get(control)?.markAsTouched();
      }
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.payment.settings.error.form"))
      return;
    } else {
      let paymentSettings: EventPaymentSettings = this.getPaymentSettingsFields();
      if (paymentSettings !== undefined && paymentSettings.eventId) {
        this.loaderService.startLoading();
        this.paymentSettingsService.updateEventPaymentSettings(paymentSettings, () => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("app.common.success.event.payment.settings.edit"))
          this.paymentSettingsSaved.emit(paymentSettings);
        }, () => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.common.server.error"))
        });
      }
    }
  }
}
